import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import instagramIcon from "../assets/images/instagram_blue.svg";
import "./PrivacyPolicy.css";

function TermsAndConditions() {
  useEffect(() => {
    const originalTitle = document.title;
    document.title = "Terms & Conditions - TLINK.ME";
    window.scrollTo(0, 0);

    return () => {
      document.title = originalTitle;
    };
  }, []);

  return (
    <div className="privacy-container">
      <header className="privacy-header">
        <div className="header-content">
          <div className="header-logo">
            <Link to="/" className="logo-link">
              <h1>TLINK.ME</h1>
            </Link>
          </div>
        </div>
      </header>

      <main className="privacy-content">
        <h1 className="privacy-title">Terms &amp; Conditions (Creators)</h1>

        <h2 className="privacy-h2">1. Acceptance of Terms</h2>
        <p className="privacy-text">
          By creating a TLINK account, clicking “I Agree,” or otherwise
          uploading content, you (“Creator,” “you,” or “your”) irrevocably
          accept and agree to be bound by these T&amp;C as of that date
          (“Effective Date”).
        </p>

        <h2 className="privacy-h2">2. Definitions</h2>
        <p className="privacy-text">
          <strong>“Account”</strong> – your user credentials on
          creators.tlink.me.
          <br />
          <strong>“Content”</strong> – any photo, video, audio, text, stream,
          AI generation, metadata, or other material uploaded or transmitted via
          TLINK.
          <br />
          <strong>“Gross Revenue”</strong> – all amounts actually collected
          from end-users for access to your TLINK profile, paid chats, media
          sales, tipping, and other monetized interactions before any deduction
          other than (i) VAT/GST or similar sales taxes remitted to authorities
          and (ii) chargebacks/refunds.
          <br />
          <strong>“Net Revenue”</strong> – Gross Revenue minus (i)
          chargebacks/refunds actually processed after the 21-day holding
          period, (ii) the TLINK platform fee, and (iii) the Telegram platform
          fee.
          <br />
          <strong>“Referral Creator”</strong> – a creator onboarded through a
          unique referral link or code you generated.
        </p>

        <h2 className="privacy-h2">3. Eligibility &amp; Verification</h2>
        <ol>
          <li>
            <strong>Age.</strong> You must be at least 18 years old and legally
            able to enter into contracts.
          </li>
          <li>
            <strong>KYC/AML.</strong> You agree to complete identity and
            sanctions screening at onboarding and before each withdrawal
            request. We may engage third-party verification services and store
            related data as permitted by our{" "}
            <Link to="/privacy-policy">Privacy Policy</Link>.
          </li>
          <li>
            <strong>Accuracy.</strong> All information you provide must be
            complete and kept current. Failure may result in suspension.
          </li>
          <li>
            <strong>Account Security.</strong> You are solely responsible for
            maintaining the confidentiality of your login credentials and for
            all activities that occur under your Account. TLINK is not liable
            for any loss or damage arising from unauthorized access to your
            Account.
          </li>
        </ol>

        <h2 className="privacy-h2">4. Services &amp; License Grants</h2>
        <p className="privacy-text">
          TLINK is not Telegram. It is a monetization tool that operates on the
          Telegram platform, enabling creators to deliver and manage paid
          content via Telegram channels and chats.
        </p>

        <h3 className="privacy-h3">Telegram Account Ownership</h3>
        <p className="privacy-text">
          For the avoidance of doubt, any Telegram account, channel, bot, or
          chat created, used, or operated by or on behalf of the Company in
          connection with this Agreement, including any such account associated
          with the Creator’s name, likeness, or digital persona, shall be the
          sole and exclusive property of the Company. The Creator shall have no
          ownership interest in, and no rights of access to, control over, use
          of, or transfer of any such Telegram account during or after the term
          of this Agreement.
        </p>

        <ol>
          <li>
            <strong>Hosting &amp; Paywall.</strong> TLINK hosts, paywalls, and
            delivers your Content to end-users on Telegram channels and chats
            you authorize.
          </li>
          <li>
            <strong>Platform License to Creator.</strong> We grant you a
            limited, revocable, non-exclusive license to access the dashboard
            and related APIs solely for legitimate creator monetization.
          </li>
          <li>
            <strong>Creator License to TLINK.</strong>
            <ol type="a">
              <li>
                <strong>Hosting License.</strong> For as long as your Account
                remains active and for so long as we maintain archival backups
                thereafter, you grant TLINK a worldwide, non-exclusive,
                royalty-free license to host, store, reproduce, distribute,
                publicly display, and perform your Content for end-users
                according to your paywall settings.
              </li>
              <li>
                <strong>Promotional License.</strong> While your Account is
                active, you grant TLINK a worldwide, royalty-free license to use
                your name, image, likeness, trademarks, and “digital persona”
                (including AI renderings) in TLINK marketing, without further
                approval.
              </li>
              <li>
                <strong>Reservation of Rights.</strong> You retain all ownership
                in the underlying Content; TLINK owns all rights in the
                Platform, its compilations, and any anonymized or aggregate
                data.
              </li>
            </ol>
          </li>
        </ol>

        <h2 className="privacy-h2">5. Creator Earnings &amp; Payment</h2>
        <ol>
          <li>
            <strong>Revenue Share.</strong> You are entitled to 50% of Gross
            Revenue generated by your profile (the “Creator Share”).
          </li>
          <li>
            <strong>Holding Period.</strong> All Gross Revenue is subject to a
            21-day clearance period to manage fraud risk and refunds.
          </li>
          <li>
            <strong>Withdrawals.</strong> After clearance, you may instruct TLINK
            to pay available balances via (i) bank wire transfer or (ii) crypto
            stable-coin (e.g., USDC on Ethereum). TLINK may impose minimum
            withdrawal amounts, network-gas fees, or banking charges, each
            disclosed in the dashboard at the time of withdrawal.
          </li>
          <li>
            <strong>Chargebacks &amp; Refunds.</strong> If an end-user obtains a
            chargeback or refund after funds were paid to you, TLINK will deduct
            the corresponding amount (plus card-scheme penalties, if any) from
            your next payout(s). You remain ultimately liable for negative
            balances.
          </li>
          <li>
            <strong>Taxes.</strong> You are solely responsible for reporting and
            paying all income, payroll, VAT, or similar taxes arising from
            Creator Share. TLINK may withhold amounts where required by law.
          </li>
          <li>
            <strong>Statements.</strong> Transaction history is available in the
            dashboard and is conclusive absent manifest error.
          </li>
        </ol>

        <h2 className="privacy-h2">6. Referral Program</h2>
        <ol>
          <li>
            <strong>Direct Referrals.</strong> For each Referral Creator, you
            earn 3% of such creator’s Gross Revenue for as long as they remain
            active unless TLINK modifies the plan (see Section 14).
          </li>
          <li>
            <strong>Cap &amp; Plan Changes.</strong> TLINK may revise
            percentages, duration, or structure at any time without notice;
            continued use constitutes acceptance. No payments accrue after plan
            termination.
          </li>
        </ol>

        <h2 className="privacy-h2">7. Content Standards &amp; Prohibited Conduct</h2>
        <ol>
          <li>
            <strong>Illegal or Harmful Content.</strong> You must not upload,
            post, or facilitate any Content that:
            <ul>
              <li>Depicts or exploits minors (real, fictional, or simulated);</li>
              <li>
                Involves bestiality, necrophilia, sexual violence, trafficking,
                non-consensual acts, incest, or other unlawful or taboo
                material;
              </li>
              <li>
                Is deepfake or otherwise manipulated without documented consent
                of depicted persons;
              </li>
              <li>Promotes hatred or violence against a protected class;</li>
              <li>Contains private personal data, revenge porn, or defamation;</li>
              <li>Infringes third-party IP or violates any law.</li>
            </ul>
          </li>
          <li>
            <strong>Brand Guidelines &amp; Link Titles.</strong> Your public
            links and channel names must comply with TLINK branding guides (e.g.,
            “My Telegram”) and must not mislead users. Repeated violations may
            result in suspension.
          </li>
          <li>
            <strong>Co-authored Content:</strong>
            <ol type="a">
              <li>
                You are legally responsible for all Content you upload. You may
                upload Content that features someone other than you
                (&quot;Co-Authored Content&quot;).
              </li>
              <li>
                You warrant that each individual in Co-Authored Content you
                upload is:
                <ol type="i">
                  <li>
                    a Creator you have tagged using the tool available on TLINK;
                    or
                  </li>
                  <li>
                    an individual at least 18 years of age whose proof of
                    identity and whose written, informed consent you have
                    obtained to share the Content on TLINK.
                  </li>
                </ol>
              </li>
              <li>
                You and each Co-Author will provide us with necessary documents
                to confirm you have satisfied the requirements of these Terms of
                Service.
              </li>
              <li>
                If you, or anyone appearing in Co-Authored Content, do not
                provide any information we request, we may restrict or delete
                the Co-Authored Content or your account, and/or withhold all or
                any portion of your Creator Earnings.
              </li>
              <li>
                We are not a party to any agreement that you have to create
                Co-Authored Content or any resulting dispute. All claims arising
                from Co-Authored Content shall be made against the Creator(s)
                who posted or who appear in the Co-Authored Content. You will
                not make any claims against us, and you release us from any
                claims against us, arising from Co-Authored Content.
              </li>
              <li>
                Any individual(s) who appear in the Co-Authored Content may, at
                any time, withdraw their consent to appear in that Co-Authored
                Content. Co-Authored Content may be deleted where a participant
                withdraws consent.
              </li>
            </ol>
          </li>
          <li>
            <strong>Brand Misrepresentation.</strong> You must not impersonate
            TLINK, falsely suggest sponsorship, or create content, links, or
            channels that imply official TLINK status.
          </li>
          <li>
            <strong>Community Compliance.</strong> You agree to comply with
            Telegram's Terms of Service at all times.
          </li>
        </ol>

        <h2 className="privacy-h2">8. Content Monitoring &amp; Moderation Policy</h2>

        <h3 className="privacy-h3">8.1 Content Standards</h3>
        <p className="privacy-text">
          All content on the TLINK platform must comply with:
        </p>
        <ul>
          <li>Applicable local and international laws</li>
          <li>Telegram platform policies</li>
          <li>Payment service provider and partner requirements</li>
          <li>TLINK internal content standards</li>
        </ul>
        <p className="privacy-text">
          Prohibited content includes, but is not limited to: hate and hate symbols, racism,
          homophobia, transphobia, sexism, misogyny, misandry, antisemitism, xenophobia,
          ethnic hatred, nationalism, cults, religion, minors, animals and bestiality, violence,
          rape, crime, threats, self harm, suicide, weapons, firearms, terrorism, human trafficking,
          blackmail, scams, illegal drugs, substance abuse, non consensual sexual themes (abduction,
          asphyxiating, choking, chloroform, comatose, coma, CP, molestation, pedophilia),
          extreme harmful fetishes (incest, necrophilia, scatology, body fluids, vomit, blood,
          pain and injury (caning, cbt, mutilation), medical, feces, menstruation, cervix, cannibalism).
        </p>

        <h3 className="privacy-h3">8.2 Automated Content Monitoring</h3>
        <p className="privacy-text">
          TLINK uses automated systems and rules-based controls to monitor content and platform
          activity, including:
        </p>
        <ul>
          <li>Keyword and pattern detection</li>
          <li>Metadata and usage analysis</li>
          <li>Bot interaction and activity monitoring</li>
          <li>Flagging of abnormal posting or engagement behavior</li>
        </ul>
        <p className="privacy-text">
          These systems are designed to identify potentially non-compliant or high-risk content for review.
        </p>

        <h3 className="privacy-h3">8.3 Manual Review &amp; Moderation</h3>
        <p className="privacy-text">
          Automated monitoring is supplemented by manual moderation. Flagged content and accounts
          are reviewed by trained staff to assess compliance with internal policies and external
          requirements.
        </p>
        <p className="privacy-text">
          Manual review may occur:
        </p>
        <ul>
          <li>During creator onboarding</li>
          <li>When content is flagged by automated systems</li>
          <li>When reports are received from users or partners</li>
          <li>In connection with payment or account reviews</li>
        </ul>

        <h3 className="privacy-h3">8.4 Creator Controls &amp; Accountability</h3>
        <p className="privacy-text">
          Creators are required to agree to platform terms and content guidelines as part of onboarding.
          Repeated violations or serious breaches may result in:
        </p>
        <ul>
          <li>Content removal</li>
          <li>Account restrictions or suspension</li>
          <li>Permanent removal from the platform</li>
        </ul>

        <h3 className="privacy-h3">8.5 Reporting &amp; Enforcement</h3>
        <p className="privacy-text">
          TLINK maintains internal processes to:
        </p>
        <ul>
          <li>Document moderation actions and decisions</li>
          <li>Escalate serious violations</li>
          <li>Cooperate with Telegram, payment processors, and authorities when legally required</li>
        </ul>

        <h3 className="privacy-h3">8.6 Ongoing Review &amp; Updates</h3>
        <p className="privacy-text">
          Content moderation processes are reviewed periodically and updated to address evolving risks,
          regulatory guidance, and platform requirements.
        </p>

        <h2 className="privacy-h2">9. Intellectual-Property Claims &amp; DMCA</h2>
        <p className="privacy-text">
          TLINK responds to valid takedown notices and may remove Content or
          disable accounts upon receipt of a lawful IP-infringement claim.
          Repeat infringers will be terminated.
        </p>

        <h2 className="privacy-h2">10. Confidentiality</h2>
        <p className="privacy-text">
          You shall keep all non-public information about TLINK’s technology,
          pricing, or operations strictly confidential, including but not
          limited to any AI or automation systems powering chats.
        </p>

        <h2 className="privacy-h2">11. Term, Termination &amp; Suspension</h2>
        <ol>
          <li>
            <strong>Term.</strong> This Agreement commences on the Effective Date
            and continues until terminated.
          </li>
          <li>
            <strong>Creator Termination.</strong> You may terminate at will by
            emailing support@tlink.me, partners@tlink.me, and
            partnerships@tlink.me. Termination is effective when TLINK confirms
            receipt or within five (5) business days, whichever is earlier.
          </li>
          <li>
            <strong>TLINK Suspension/Termination.</strong> TLINK may, in its sole
            discretion and without notice, suspend payouts, remove Content, or
            terminate your Account for any reason, including suspected breach,
            fraud, or reputational risk.
          </li>
          <li>
            <strong>Post-Termination Rights.</strong>
            <ul>
              <li>
                <strong>Earnings.</strong> Cleared balances will be paid on the
                next scheduled payout cycle. Balances subject to ongoing disputes
                or potential chargebacks will be held until resolved.
              </li>
              <li>
                <strong>Content Retention.</strong> TLINK may retain server copies
                and archival backups for security, compliance, or evidence
                purposes unless you specifically request permanent deletion in
                writing.
              </li>
              <li>
                <strong>Survival.</strong> Sections 5, 6, 7, 8, 9, 10, 12, 13, and
                15–19 survive termination.
              </li>
            </ul>
          </li>
        </ol>

        <h2 className="privacy-h2">12. Modifications to T&amp;C</h2>
        <p className="privacy-text">
          TLINK may modify these T&amp;C (including revenue terms) at any time
          without prior notice. Updated terms take effect upon posting to
          creators.tlink.me, and your continued use constitutes acceptance. If
          you disagree, your sole remedy is to terminate your Account under
          Section 11.2.
        </p>

        <h2 className="privacy-h2">13. Warranties &amp; Disclaimers</h2>
        <ol>
          <li>
            <strong>Creator Warranty.</strong> You represent and warrant that:
            (i) you own or have lawful rights to all Content; (ii) all persons
            depicted are adults who have consented in writing; and (iii)
            uploading and monetizing the Content through TLINK does not violate
            any law or third-party right.
          </li>
          <li>
            <strong>Platform Disclaimer.</strong> THE PLATFORM IS PROVIDED “AS
            IS” WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. TLINK DOES
            NOT WARRANT UNINTERRUPTED SERVICE, ERROR-FREE OPERATION, OR
            PROFITABILITY.
          </li>
        </ol>

        <h2 className="privacy-h2">14. Limitation of Liability</h2>
        <p className="privacy-text">
          To the maximum extent permitted by law, TLINK, its affiliates,
          officers, and employees shall not be liable for indirect, incidental,
          special, consequential, or punitive damages, or for lost profits, even
          if advised of the possibility. TLINK’s aggregate liability under these
          T&amp;C shall not exceed the total Creator Share actually paid to you
          during the six-month period immediately preceding the claim.
        </p>

        <h2 className="privacy-h2">15. Indemnification</h2>
        <p className="privacy-text">
          You will defend, indemnify, and hold harmless TLINK and its affiliates
          against any third-party claim arising out of (i) your Content, (ii)
          your breach of these T&amp;C, or (iii) your violation of law.
        </p>

        <h2 className="privacy-h2">16. Governing Law &amp; Dispute Resolution</h2>
        <ol>
          <li>
            <strong>Governing Law.</strong> These T&amp;C are governed by the
            laws of Cyprus, excluding conflict-of-law rules.
          </li>
          <li>
            <strong>Arbitration.</strong> Any dispute shall be finally settled by
            binding arbitration under the Cyprus Arbitration &amp; Mediation
            Centre Rules. Seat: Nicosia. Language: English. Judgment on the award
            may be entered in any court of competent jurisdiction.
          </li>
          <li>
            <strong>Class-Action Waiver.</strong> You and TLINK waive any right to
            litigate or arbitrate claims on a class, collective, or
            representative basis.
          </li>
          <li>
            <strong>Injunctive Relief.</strong> Nothing herein prevents TLINK from
            seeking equitable relief for IP or confidentiality breaches in any
            competent court.
          </li>
        </ol>

        <h2 className="privacy-h2">17. Relationship of Parties</h2>
        <p className="privacy-text">
          The parties are independent contractors. Nothing creates a partnership,
          joint venture, franchise, or employment relationship.
        </p>

        <h2 className="privacy-h2">18. Assignment</h2>
        <p className="privacy-text">
          You may not assign or transfer your rights without TLINK’s prior
          written consent. TLINK may freely assign these T&amp;C.
        </p>

        <h2 className="privacy-h2">19. Notices</h2>
        <p className="privacy-text">
          Notices to TLINK must be sent to legal@tlink.me with a paper copy to
          the registered address of TLINK. TLINK may provide notices to you
          via email or dashboard alert.
        </p>

        <h2 className="privacy-h2">20. Entire Agreement; Severability; Waiver</h2>
        <p className="privacy-text">
          These T&amp;C (including any policies linked or incorporated by
          reference) constitute the entire agreement and supersede all prior
          understandings relating to the subject matter. If any provision is
          held invalid, the remainder remains enforceable. TLINK's failure to
          enforce any provision is not a waiver.
        </p>

        <div className="policy-navigation">
          <h3 className="policy-nav-title">Other Policies</h3>
          <div className="policy-nav-links">
            <Link to="/privacy-policy" className="policy-nav-link">
              Privacy Policy
            </Link>
            <Link to="/compliance-policy" className="policy-nav-link">
              Compliance Policy
            </Link>
            <Link to="/pii-policy" className="policy-nav-link">
              PII Policy
            </Link>
          </div>
        </div>
      </main>

      <footer className="privacy-footer">
        <div className="footer-content">
          <div className="footer-left">
            <span className="footer-title">FOLLOW US</span>
            <div className="footer-social">
              <img
                src={instagramIcon}
                alt="Instagram"
                className="footer-instagram-icon"
              />
              <a
                href="https://instagram.com/tlink.me"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-handle"
              >
                TLINK.ME
              </a>
            </div>
          </div>
          <div className="footer-center">
            <div className="footer-logo">TLINK.ME</div>
          </div>
          <div className="footer-right">
            <div className="footer-copyright">
              ALL RIGHTS RESERVED.
              <span className="footer-year">2025</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default TermsAndConditions;
